import { type Ref } from 'vue';
import { usePagedList, type Page } from '@/composables/usePagedList';
import { usePausedSearch } from '@/composables/usePausedSearch';

interface SearchableListConfig<T> {
    // The slot the search text lives in.
    keyword: Ref<string>;
    // A first page (url null) is built from whatever the caller's filters hold, the keyword among
    // them; a page url carries the query of the fetch that produced it.
    fetchPage: (url: string | null) => Promise<Page<T> | undefined | void>;
    // Something to do as the text changes, before any request: drop a selection, say.
    onTyped?: () => void;
}

/**
 * A paged list with a search box: usePagedList, and a search that waits for the user to pause
 * (usePausedSearch).
 *
 * Used as it is by a list that is always on screen (the rule picker, the guided sources step);
 * a browser drawer adds its opening and closing (useBrowserList). useElementGrid has its own
 * search, because there the text is one of the predicates its watcher fetches for.
 */
export function useSearchableList<T>(config: SearchableListConfig<T>) {
    const list = usePagedList<T>((url) => {
        // A first page is built from the current filters, typed text included, so a search still
        // waiting on its timer would only repeat it. A page url is different: it carries the query
        // of the fetch that produced it, so the pending search must still run after it.
        if (!url) cancelSearch();
        return config.fetchPage(url);
    });

    const { onSearch, cancelSearch } = usePausedSearch({
        keyword: config.keyword,
        onTyped: config.onTyped,
        search: () => { list.fetch(); },
    });

    return { ...list, onSearch, cancelSearch };
}
